import React from 'react'
import { useNavigate } from 'react-router-dom'
import LoadingSpinner from '../loading/loading_spinner'
import { EnterpriseSummary, EnumData } from '../../clients/kyousou_api_client'
import BaseCard from './base_card'
import FlatButton from '../buttons/flat_button'
import FavoriteButton from '../buttons/favorite_button'
import { useEnterpriseFavorite } from '../../hooks/use_enterprise_favorite'
import { useAuthAuthorized } from '../../hooks/auth/use_auth'
import MatchingRateBadge from '../badges/matching_rate_badge'

interface AsiatoEntry {
  displayCount: number
  stayTimeText: string
  latestDisplayAt: string
  enterprise: EnterpriseSummary
}

interface EnterpriseStatCardProps {
  asiato: AsiatoEntry
  initialized: boolean
}

const EnterpriseStatCard: React.FC<EnterpriseStatCardProps> = ({
  asiato,
  initialized,
}) => {
  const navigate = useNavigate()
  const { isFavorited, toggleFavorite } = useEnterpriseFavorite(
    asiato.enterprise.id,
    asiato.enterprise.isFavorited
  )
  const { user } = useAuthAuthorized()

  if (!initialized) {
    return <LoadingSpinner className="my-20" />
  }

  const handleViewDetails = () => {
    navigate(`/enterprises/detail/${asiato.enterprise.id}`)
  }

  const handleSendMessage = () => {
    navigate('/chat')
  }

  // 業界名を安全に取得する関数
  const getIndustryName = (industryParentCode: EnumData): string => {
    return industryParentCode?.value || 'その他'
  }

  return (
    <BaseCard>
      <div className="flex flex-row items-center relative pb-12 xl:pb-0">
        {/* 左側：ロゴ＋会社名・業種・都道府県 */}
        <div className="flex items-center w-[300px]">
          <img
            src={asiato.enterprise.logoUrl}
            alt="企業ロゴ"
            className="w-12 h-12 rounded object-cover flex-shrink-0 mr-3"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2">
              <div className="font-bold text-base flex-1 min-w-0">
                {asiato.enterprise.name}{' '}
                {/* <span className="text-xs text-main-darkgray">
                  {asiato.enterprise.nameEn}
                </span> */}
              </div>
              <div className="flex-shrink-0">
                <FavoriteButton
                  isFavorited={isFavorited}
                  onClick={toggleFavorite}
                  isOwn={user.kuchikomiEnterpriseId === asiato.enterprise.id}
                />
              </div>
            </div>
            <div className="flex gap-2 text-xs text-main-darkgray">
              <span className="flex items-center gap-1">
                <span className="material-icons !text-base align-middle">
                  domain
                </span>
                {getIndustryName(asiato.enterprise.industryParentCode)}
              </span>
              <span className="flex items-center gap-1">
                <span className="material-icons !text-base align-middle">
                  location_on
                </span>
                {asiato.enterprise.prefecture}
              </span>
            </div>
          </div>
        </div>

        {/* 右側：訪問回数・最終訪問・滞在時間・マッチング率・ボタン */}
        <div className="xl:flex xl:flex-row gap-2 ml-auto">
          <div className="flex">
            <div className="flex flex-col items-center min-w-[90px]">
              <div className="flex items-center gap-1 h-6">
                <span className="material-icons !text-base align-middle text-main-darkgray">
                  visibility
                </span>
                <span className="text-xs text-main-darkgray">訪問回数</span>
              </div>
              <div className="flex items-center justify-center h-8 mt-1">
                <span className="px-3 py-1 rounded-full border border-main-green text-main-green text-sm font-bold bg-main-white">
                  {asiato.displayCount}回
                </span>
              </div>
            </div>

            <div className="flex flex-col items-center min-w-[120px]">
              <div className="flex items-center gap-1 h-6">
                <span className="material-icons !text-base align-middle text-main-darkgray">
                  calendar_today
                </span>
                <span className="text-xs text-main-darkgray">最終訪問</span>
              </div>
              <div className="flex items-center justify-center h-8 mt-1">
                <span className="text-sm">
                  {asiato.latestDisplayAt?.slice(0, 16).replace('T', ' ') ??
                    '-'}
                </span>
              </div>
            </div>

            <div className="flex flex-col items-center min-w-[90px]">
              <div className="flex items-center gap-1 h-6">
                <span className="material-icons !text-base align-middle text-main-darkgray">
                  timer
                </span>
                <span className="text-xs text-main-darkgray">滞在時間</span>
              </div>
              <div className="flex items-center justify-center h-8 mt-1">
                <span className="text-sm">{asiato.stayTimeText}</span>
              </div>
            </div>

            <div className="flex flex-col items-center min-w-[90px]">
              <div className="flex items-center gap-1 h-6">
                <span className="material-icons !text-base align-middle text-main-darkgray">
                  favorite
                </span>
                <span className="text-xs text-main-darkgray">マッチング率</span>
              </div>
              <div className="mt-1">
                <MatchingRateBadge
                  matchingRate={asiato.enterprise.matchingRate}
                  isOwn={user.kuchikomiEnterpriseId === asiato.enterprise.id}
                  showLabel={false}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 absolute bottom-0 right-0 xl:static">
            <FlatButton
              onClick={handleViewDetails}
              outline
              className="!p-2 text-sm xl:text-base"
            >
              <span className="flex items-center">
                <span className="material-icons !text-base align-middle mr-2">
                  open_in_new
                </span>
                詳細を見る
              </span>
            </FlatButton>
            <FlatButton
              onClick={handleSendMessage}
              className="!p-2 text-sm xl:text-base"
            >
              メッセージを送る
            </FlatButton>
          </div>
        </div>
      </div>
    </BaseCard>
  )
}

export default EnterpriseStatCard
